import {
  DocumentData,
  Firestore,
  QueryConstraint,
  QueryDocumentSnapshot,
  collection as firestoreCollection,
  getDocs as firestoreGetDocs,
  limit as firestoreLimit,
  orderBy as firestoreOrderBy,
  query as firestoreQuery,
  startAfter as firestoreStartAfter,
  where as firestoreWhere
} from 'firebase/firestore';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ILogRouteNavigationPayload,
  IUserEventLog,
  UserEventLogEventType
} from '@simplyfly/shared';
import { FunctionsService } from '../functions/functions.service';
import { AppDataService } from 'src/app/services/data/app-data.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FIREBASE_FIRESTORE } from '../firebase/firebase.providers';

export type UserEventLogCursor =
  | QueryDocumentSnapshot<DocumentData>
  | null;

export type UserEventLogTrackingStatus = 'idle' | 'skipped' | 'success' | 'error';

export interface IUserEventLogTrackingResult {
  status: UserEventLogTrackingStatus;
  at: Date;
  routeFull: string | null;
  reason?: string;
  error?: any;
}

interface ITrackedPageContext {
  routeFull: string;
  routePath: string;
  startedAtMs: number;
}

interface IQueuedTrackingEvent {
  id: string;
  userId: string;
  payload: ILogRouteNavigationPayload;
  enqueuedAtMs: number;
}

type SessionEndReason = 'logout' | 'user-change';

@Injectable({
  providedIn: 'root'
})
export class UserEventLogsService {
  private static readonly sessionStorageKey = 'user-event-logs-session-id';
  private static readonly tabIdStorageKey = 'user-event-logs-tab-id';
  private static readonly lastTrackedRouteStorageKey = 'user-event-logs-last-tracked-route';
  private static readonly activeTrackedPageStorageKey = 'user-event-logs-active-tracked-page';
  private static readonly heartbeatIntervalMs = 60 * 1000;
  private static readonly eventQueueStorageKey = 'user-event-logs-event-queue';
  private static readonly eventQueueFlushIntervalMs = 10 * 1000;
  private static readonly eventQueueBatchSize = 50;
  private static readonly eventQueueMaxLength = 2000;
  private static readonly tabIdPrefix = 'user-event-tab';

  private currentUserId: string | null = null;
  private isLogged = false;
  private tabId: string | null = null;

  private previousRoutePath: string | null = null;
  private lastTrackedRouteFull: string | null = null;
  private trackedPageContext: ITrackedPageContext | null = null;
  private heartbeatIntervalId: ReturnType<typeof setInterval> | null = null;
  private sendingHeartbeat = false;
  private pendingEventQueue: IQueuedTrackingEvent[] = [];
  private eventQueueFlushIntervalId: ReturnType<typeof setInterval> | null = null;
  private flushingEventQueue = false;
  private batchCallableAvailable: boolean | null = null;
  private trackingResultBehavior: BehaviorSubject<IUserEventLogTrackingResult>;
  readonly trackingResultObservable: Observable<IUserEventLogTrackingResult>;

  constructor(
    private authService: AuthService,
    @Inject(FIREBASE_FIRESTORE)
    private firestore: Firestore,
    private functionsService: FunctionsService,
    private appDataService: AppDataService
  ) {
    this.trackingResultBehavior = new BehaviorSubject<IUserEventLogTrackingResult>({
      status: 'idle',
      at: new Date(),
      routeFull: null
    });
    this.trackingResultObservable = this.trackingResultBehavior.asObservable();

    if (typeof window !== 'undefined') {
      this.pendingEventQueue = this.getStoredPendingEventQueue();
      this.startEventQueueFlushTimer();
    }

    this.authService.isLoggedObservable.subscribe((isLogged: boolean) => {
      const wasLogged = this.isLogged;
      this.isLogged = !!isLogged;

      if (!this.isLogged) {
        void this.trackSessionEnd('logout', true);
        this.currentUserId = null;
        this.resetRuntimeTrackingState(true);
        return;
      }

      if (!wasLogged && this.currentUserId) {
        setTimeout(() => {
          this.trackNavigation();
        }, 0);
      }
    });

    this.authService.userObservable.subscribe(user => {
      const nextUserId = user?.id || null;

      if (this.currentUserId !== nextUserId) {
        if (this.currentUserId && this.isLogged) {
          void this.trackSessionEnd('user-change');
        }

        this.currentUserId = nextUserId;
        this.resetRuntimeTrackingState(true);

        if (this.currentUserId && this.isLogged) {
          setTimeout(() => {
            this.trackNavigation();
          }, 0);
        }
      }
    });
  }

  async trackNavigation(): Promise<IUserEventLogTrackingResult> {
    const routeFull = this.getCurrentRouteFull();
    const routePath = this.getCurrentRoutePath();

    if (!this.isLogged || !this.currentUserId) {
      return this.setTrackingResult({
        status: 'skipped',
        routeFull: routeFull || null,
        reason: 'missing-login-or-user-id'
      });
    }

    if (!routeFull || !routePath) {
      return this.setTrackingResult({
        status: 'skipped',
        routeFull: routeFull || null,
        reason: 'route-not-available'
      });
    }

    this.ensureTrackedPageContextLoadedFromStorage();

    if (!this.lastTrackedRouteFull) {
      this.lastTrackedRouteFull = this.getStoredLastTrackedRouteForCurrentUser();
    }

    if (this.trackedPageContext && this.trackedPageContext.routeFull !== routeFull) {
      await this.trackPageLeave(this.trackedPageContext);
      this.trackedPageContext = null;
      this.clearStoredTrackedPageContext();
    }

    if (this.lastTrackedRouteFull === routeFull) {
      if (!this.trackedPageContext) {
        this.trackedPageContext = {
          routeFull,
          routePath,
          startedAtMs: Date.now()
        };
        this.setStoredTrackedPageContextForCurrentUser(this.trackedPageContext);
      }

      this.previousRoutePath = routePath;
      this.startHeartbeatTimer();

      return this.setTrackingResult({
        status: 'skipped',
        routeFull,
        reason: 'duplicate-route'
      });
    }

    const nextTrackedPageContext: ITrackedPageContext = {
      routeFull,
      routePath,
      startedAtMs: Date.now()
    };

    try {
      await this.sendTrackingEvent({
        eventType: 'page_enter',
        routeFull,
        routePath,
        previousRoutePath: this.previousRoutePath
      });

      this.previousRoutePath = routePath;
      this.lastTrackedRouteFull = routeFull;
      this.setStoredLastTrackedRouteForCurrentUser(routeFull);
      this.trackedPageContext = nextTrackedPageContext;
      this.setStoredTrackedPageContextForCurrentUser(nextTrackedPageContext);
      this.startHeartbeatTimer();

      return this.setTrackingResult({
        status: 'success',
        routeFull
      });
    } catch (err: any) {
      return this.setTrackingResult({
        status: 'error',
        routeFull,
        reason: err?.message || err?.code || 'unknown-error',
        error: err
      });
    }
  }

  private async trackPageLeave(trackedPageContext: ITrackedPageContext): Promise<void> {
    const pageDurationMs = Math.max(0, Date.now() - trackedPageContext.startedAtMs);

    try {
      await this.sendTrackingEvent({
        eventType: 'page_leave',
        routeFull: trackedPageContext.routeFull,
        routePath: trackedPageContext.routePath,
        previousRoutePath: this.previousRoutePath,
        pageDurationMs,
        pageStartedAtMs: trackedPageContext.startedAtMs
      });
    } catch {
      // Keep navigation tracking resilient even when leave events fail.
    }
  }

  private async trackSessionEnd(reason: SessionEndReason, force: boolean = false): Promise<void> {
    if ((!this.isLogged && !force) || !this.currentUserId) {
      return;
    }

    this.ensureTrackedPageContextLoadedFromStorage();

    if (!this.trackedPageContext) {
      this.stopHeartbeatTimer();
      this.clearStoredTrackedPageContext();
      return;
    }

    const pageDurationMs = Math.max(0, Date.now() - this.trackedPageContext.startedAtMs);

    try {
      await this.sendTrackingEvent({
        eventType: 'session_end',
        routeFull: this.trackedPageContext.routeFull,
        routePath: this.trackedPageContext.routePath,
        previousRoutePath: this.previousRoutePath,
        pageDurationMs,
        pageStartedAtMs: this.trackedPageContext.startedAtMs,
        sessionEndReason: reason
      });
    } catch {
      // Session end is best-effort.
    } finally {
      this.trackedPageContext = null;
      this.clearStoredTrackedPageContext();
      this.stopHeartbeatTimer();
    }
  }

  private async sendHeartbeat(): Promise<void> {
    if (
      this.sendingHeartbeat ||
      !this.isLogged ||
      !this.currentUserId
    ) {
      return;
    }

    this.ensureTrackedPageContextLoadedFromStorage();

    if (!this.trackedPageContext) {
      this.stopHeartbeatTimer();
      return;
    }

    const currentRouteFull = this.getCurrentRouteFull();
    if (currentRouteFull && currentRouteFull !== this.trackedPageContext.routeFull) {
      await this.trackNavigation();
      return;
    }

    this.sendingHeartbeat = true;

    try {
      await this.sendTrackingEvent({
        eventType: 'page_heartbeat',
        routeFull: this.trackedPageContext.routeFull,
        routePath: this.trackedPageContext.routePath,
        previousRoutePath: this.previousRoutePath,
        pageDurationMs: Math.max(0, Date.now() - this.trackedPageContext.startedAtMs),
        pageStartedAtMs: this.trackedPageContext.startedAtMs
      });
    } catch {
      // Heartbeat is best-effort.
    } finally {
      this.sendingHeartbeat = false;
    }
  }

  private startHeartbeatTimer(): void {
    if (typeof window === 'undefined' || this.heartbeatIntervalId) {
      return;
    }

    this.heartbeatIntervalId = setInterval(() => {
      void this.sendHeartbeat();
    }, UserEventLogsService.heartbeatIntervalMs);
  }

  private stopHeartbeatTimer(): void {
    if (!this.heartbeatIntervalId) {
      return;
    }

    clearInterval(this.heartbeatIntervalId);
    this.heartbeatIntervalId = null;
  }

  private resetRuntimeTrackingState(clearStorage: boolean): void {
    this.previousRoutePath = null;
    this.lastTrackedRouteFull = null;
    this.trackedPageContext = null;
    this.stopHeartbeatTimer();

    if (clearStorage) {
      this.clearStoredLastTrackedRoute();
      this.clearStoredTrackedPageContext();
    }
  }

  private ensureTrackedPageContextLoadedFromStorage(): void {
    if (this.trackedPageContext) {
      return;
    }

    this.trackedPageContext = this.getStoredTrackedPageContextForCurrentUser();
  }

  private async sendTrackingEvent(params: {
    eventType: UserEventLogEventType;
    routeFull: string;
    routePath: string;
    previousRoutePath?: string | null;
    pageDurationMs?: number | null;
    pageStartedAtMs?: number | null;
    sessionEndReason?: SessionEndReason;
  }): Promise<void> {
    const payload: ILogRouteNavigationPayload = {
      routeFull: params.routeFull,
      routePath: params.routePath,
      previousRoutePath: params.previousRoutePath ?? null,
      sessionId: this.getSessionId(),
      tabId: this.getTabId(),
      eventType: params.eventType,
      pageDurationMs: params.pageDurationMs ?? null,
      pageStartedAt:
        typeof params.pageStartedAtMs === 'number'
          ? new Date(params.pageStartedAtMs).toISOString()
          : null,
      sessionEndReason: params.sessionEndReason || null,
      userAgent:
        typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : null,
      language: typeof navigator !== 'undefined' && navigator.language ? navigator.language : null,
      timezone:
        typeof Intl !== 'undefined'
          ? Intl.DateTimeFormat().resolvedOptions().timeZone || null
          : null,
      environment: environment.environmentType || 'unknown',
      origin: typeof window !== 'undefined' ? window.location.origin || null : null,
      viewport:
        typeof window !== 'undefined'
          ? {
              width: window.innerWidth || null,
              height: window.innerHeight || null
            }
          : null,
      screen:
        typeof window !== 'undefined'
          ? {
              width: window.screen?.width || null,
              height: window.screen?.height || null,
              pixelRatio: window.devicePixelRatio || null
            }
          : null,
      clientTimestamp: new Date().toISOString()
    };

    if (!this.currentUserId) {
      return;
    }

    this.enqueueTrackingEvent(this.currentUserId, payload);
  }

  private enqueueTrackingEvent(userId: string, payload: ILogRouteNavigationPayload): void {
    const queueEvent: IQueuedTrackingEvent = {
      id: this.generateIdentifier(),
      userId,
      payload,
      enqueuedAtMs: Date.now()
    };

    this.pendingEventQueue.push(queueEvent);

    if (this.pendingEventQueue.length > UserEventLogsService.eventQueueMaxLength) {
      this.pendingEventQueue.splice(
        0,
        this.pendingEventQueue.length - UserEventLogsService.eventQueueMaxLength
      );
    }

    this.setStoredPendingEventQueue(this.pendingEventQueue);
  }

  private startEventQueueFlushTimer(): void {
    if (typeof window === 'undefined' || this.eventQueueFlushIntervalId) {
      return;
    }

    this.eventQueueFlushIntervalId = setInterval(() => {
      void this.flushPendingEventQueue();
    }, UserEventLogsService.eventQueueFlushIntervalMs);
  }

  private async flushPendingEventQueue(): Promise<void> {
    if (
      this.flushingEventQueue ||
      !this.isLogged ||
      !this.currentUserId ||
      !this.pendingEventQueue.length
    ) {
      return;
    }

    const pendingEventsForCurrentUser = this.pendingEventQueue.filter(
      queuedEvent => queuedEvent.userId === this.currentUserId
    );

    if (!pendingEventsForCurrentUser.length) {
      return;
    }

    const batch = pendingEventsForCurrentUser.slice(0, UserEventLogsService.eventQueueBatchSize);

    this.flushingEventQueue = true;

    try {
      let batchSent = false;

      if (this.batchCallableAvailable !== false) {
        try {
          await this.functionsService.callFunctionAsPromise('logUserRouteEventsBatch', {
            events: batch.map(queuedEvent => queuedEvent.payload)
          });
          this.batchCallableAvailable = true;
          batchSent = true;
        } catch (batchErr: any) {
          if (!this.shouldFallbackToSingleEventCallable(batchErr)) {
            throw batchErr;
          }

          this.batchCallableAvailable = false;
        }
      }

      if (!batchSent) {
        await this.sendEventsWithSingleCallable(batch);
      }

      const batchIds = new Set(batch.map(queuedEvent => queuedEvent.id));
      this.pendingEventQueue = this.pendingEventQueue.filter(
        queuedEvent => !batchIds.has(queuedEvent.id)
      );
      this.setStoredPendingEventQueue(this.pendingEventQueue);
    } catch (err: any) {
      this.setTrackingResult({
        status: 'error',
        routeFull: batch[batch.length - 1]?.payload?.routeFull || null,
        reason: err?.message || err?.code || 'batch-send-failed',
        error: err
      });
    } finally {
      this.flushingEventQueue = false;
    }
  }

  private async sendEventsWithSingleCallable(batch: IQueuedTrackingEvent[]): Promise<void> {
    for (const queuedEvent of batch) {
      await this.functionsService.callFunctionAsPromise('logUserRouteEvent', queuedEvent.payload);
    }
  }

  private shouldFallbackToSingleEventCallable(err: any): boolean {
    const code = `${err?.code || ''}`.toLowerCase();
    const message = `${err?.message || ''}`.toLowerCase();

    return (
      code.includes('not-found') ||
      message.includes('404') ||
      message.includes('failed to fetch') ||
      message.includes('cors')
    );
  }

  private shouldFallbackToRecentRouteScan(err: any): boolean {
    const code = `${err?.code || ''}`.toLowerCase();
    const message = `${err?.message || ''}`.toLowerCase();

    return code.includes('failed-precondition') || message.includes('requires an index');
  }

  async getPaginatedByUserId(
    userId: string,
    pageSize: number = 50,
    cursor: UserEventLogCursor = null
  ): Promise<{
    events: IUserEventLog[];
    cursor: UserEventLogCursor;
    hasMore: boolean;
  }> {
    const constraints: QueryConstraint[] = [
      this.firestoreWhere('userId', '==', userId),
      this.firestoreOrderBy('createdAt', 'desc'),
      this.firestoreLimit(pageSize)
    ];
    if (cursor) {
      constraints.push(this.firestoreStartAfter(cursor));
    }
    const querySnapshot = await this.getDocsInContext(
      this.firestoreQuery(this.firestoreCollection('userEventLogs'), ...constraints)
    );

    const events: IUserEventLog[] = querySnapshot.docs.map(doc => this.mapDocToEvent(doc));

    const nextCursor: UserEventLogCursor = querySnapshot.docs.length
      ? querySnapshot.docs[querySnapshot.docs.length - 1]
      : cursor;

    return {
      events,
      cursor: nextCursor,
      hasMore: querySnapshot.docs.length === pageSize
    };
  }

  async getPaginatedByUserIdAndDay(
    userId: string,
    dayStart: Date,
    pageSize: number = 50,
    cursor: UserEventLogCursor = null
  ): Promise<{
    events: IUserEventLog[];
    cursor: UserEventLogCursor;
    hasMore: boolean;
  }> {
    const normalizedDayStart = new Date(dayStart);
    normalizedDayStart.setHours(0, 0, 0, 0);

    const dayEnd = new Date(normalizedDayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);

    const constraints: QueryConstraint[] = [
      this.firestoreWhere('userId', '==', userId),
      this.firestoreWhere('createdAt', '>=', normalizedDayStart),
      this.firestoreWhere('createdAt', '<', dayEnd),
      this.firestoreOrderBy('createdAt', 'desc'),
      this.firestoreLimit(pageSize)
    ];
    if (cursor) {
      constraints.push(this.firestoreStartAfter(cursor));
    }
    const querySnapshot = await this.getDocsInContext(
      this.firestoreQuery(this.firestoreCollection('userEventLogs'), ...constraints)
    );

    const events: IUserEventLog[] = querySnapshot.docs.map(doc => this.mapDocToEvent(doc));

    const nextCursor: UserEventLogCursor = querySnapshot.docs.length
      ? querySnapshot.docs[querySnapshot.docs.length - 1]
      : cursor;

    return {
      events,
      cursor: nextCursor,
      hasMore: querySnapshot.docs.length === pageSize
    };
  }

  async getAllByUserIdAndDay(
    userId: string,
    dayStart: Date,
    pageSize: number = 50
  ): Promise<IUserEventLog[]> {
    let cursor: UserEventLogCursor = null;
    let hasMore = true;
    const allEvents: IUserEventLog[] = [];

    while (hasMore) {
      const result = await this.getPaginatedByUserIdAndDay(userId, dayStart, pageSize, cursor);

      if (result.events.length) {
        allEvents.push(...result.events);
      }

      hasMore = result.hasMore;
      cursor = result.cursor;
    }

    return allEvents;
  }

  async getLatestByRoutePath(routePath: string, limitCount: number = 20): Promise<IUserEventLog[]> {
    const normalizedRoutePath = this.normalizeRoutePath(routePath);

    if (!normalizedRoutePath) {
      return [];
    }

    const safeLimit = Math.max(1, Math.round(limitCount));

    try {
      const querySnapshot = await this.getDocsInContext(
        this.firestoreQuery(
          this.firestoreCollection('userEventLogs'),
          this.firestoreWhere('routePath', '==', normalizedRoutePath),
          this.firestoreOrderBy('createdAt', 'desc'),
          this.firestoreLimit(safeLimit)
        )
      );

      return querySnapshot.docs.map(doc => this.mapDocToEvent(doc));
    } catch (err: any) {
      if (!this.shouldFallbackToRecentRouteScan(err)) {
        throw err;
      }

      const fallbackSnapshot = await this.getDocsInContext(
        this.firestoreQuery(
          this.firestoreCollection('userEventLogs'),
          this.firestoreOrderBy('createdAt', 'desc'),
          this.firestoreLimit(Math.max(100, safeLimit * 20))
        )
      );

      return fallbackSnapshot.docs
        .map(doc => this.mapDocToEvent(doc))
        .filter(eventLog => this.normalizeRoutePath(eventLog.routePath) === normalizedRoutePath)
        .sort(
          (eventA, eventB) => this.getEventTimestampMs(eventB) - this.getEventTimestampMs(eventA)
        )
        .slice(0, safeLimit);
    }
  }

  async getPaginatedByRoutePath(
    routePath: string,
    pageSize: number = 10,
    cursor: UserEventLogCursor = null
  ): Promise<{
    events: IUserEventLog[];
    cursor: UserEventLogCursor;
    hasMore: boolean;
  }> {
    const normalizedRoutePath = this.normalizeRoutePath(routePath);

    if (!normalizedRoutePath) {
      return {
        events: [],
        cursor,
        hasMore: false
      };
    }

    const safePageSize = Math.max(1, Math.round(pageSize));

    const constraints: QueryConstraint[] = [
      this.firestoreWhere('routePath', '==', normalizedRoutePath),
      this.firestoreOrderBy('createdAt', 'desc'),
      this.firestoreLimit(safePageSize)
    ];
    if (cursor) {
      constraints.push(this.firestoreStartAfter(cursor));
    }
    const querySnapshot = await this.getDocsInContext(
      this.firestoreQuery(this.firestoreCollection('userEventLogs'), ...constraints)
    );

    const events: IUserEventLog[] = querySnapshot.docs.map(doc => this.mapDocToEvent(doc));

    const nextCursor: UserEventLogCursor = querySnapshot.docs.length
      ? querySnapshot.docs[querySnapshot.docs.length - 1]
      : cursor;

    return {
      events,
      cursor: nextCursor,
      hasMore: querySnapshot.docs.length === safePageSize
    };
  }

  getCurrentSessionId(): string | null {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }

    return window.sessionStorage.getItem(UserEventLogsService.sessionStorageKey);
  }

  private mapDocToEvent(
    doc: QueryDocumentSnapshot<DocumentData>
  ): IUserEventLog {
    const data = doc.data() as DocumentData;

    return {
      id: doc.id,
      userId: data.userId,
      authUid: data.authUid || null,
      eventType: data.eventType || null,
      pageDurationMs: this.toNumber(data.pageDurationMs),
      pageStartedAt: this.toDate(data.pageStartedAt),
      pageEndedAt: this.toDate(data.pageEndedAt),
      sessionEndReason: data.sessionEndReason || null,
      ipAddress: data.ipAddress || null,
      osName: data.osName || null,
      osVersion: data.osVersion || null,
      browserName: data.browserName || null,
      browserVersion: data.browserVersion || null,
      deviceType: data.deviceType || null,
      createdAt: this.toDate(data.createdAt),
      clientTimestamp: this.toDate(data.clientTimestamp),
      routeFull: data.routeFull || '',
      routePath: data.routePath || '',
      previousRoutePath: data.previousRoutePath || null,
      sessionId: data.sessionId || null,
      tabId: data.tabId || null,
      userAgent: data.userAgent || null,
      language: data.language || null,
      timezone: data.timezone || null,
      environment: data.environment || null,
      origin: data.origin || null,
      viewport: data.viewport || null,
      screen: data.screen || null,
      geo: data.geo || null
    };
  }

  private toNumber(value: any): number | null {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return null;
    }

    return value;
  }

  private toDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value?.toDate === 'function') {
      return value.toDate();
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  private getCurrentRouteFull(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    const pathname = window.location.pathname || '/';
    const search = window.location.search || '';
    const hash = window.location.hash || '';

    return `${pathname}${search}${hash}`;
  }

  private getCurrentRoutePath(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    return window.location.pathname || '/';
  }

  private normalizeRoutePath(routePath: string | null | undefined): string {
    const route = `${routePath || ''}`.trim();

    if (!route) {
      return '';
    }

    if (/^https?:\/\//i.test(route)) {
      try {
        return new URL(route).pathname || '/';
      } catch {
        return '';
      }
    }

    const routeWithoutQuery = route.split('?')[0].split('#')[0];

    if (!routeWithoutQuery) {
      return '/';
    }

    return routeWithoutQuery.startsWith('/') ? routeWithoutQuery : `/${routeWithoutQuery}`;
  }

  private getEventTimestampMs(eventLog: IUserEventLog): number {
    if (eventLog.createdAt instanceof Date) {
      return eventLog.createdAt.getTime();
    }

    if (eventLog.clientTimestamp instanceof Date) {
      return eventLog.clientTimestamp.getTime();
    }

    return 0;
  }

  private getSessionId(): string {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return this.generateSessionId();
    }

    let sessionId = window.sessionStorage.getItem(UserEventLogsService.sessionStorageKey);

    if (!sessionId) {
      sessionId = this.generateSessionId();
      window.sessionStorage.setItem(UserEventLogsService.sessionStorageKey, sessionId);
    }

    return sessionId;
  }

  private getTabId(): string {
    if (this.tabId) {
      return this.tabId;
    }

    if (typeof window !== 'undefined' && window.sessionStorage) {
      let storedTabId = window.sessionStorage.getItem(UserEventLogsService.tabIdStorageKey);

      if (!storedTabId) {
        storedTabId = `${UserEventLogsService.tabIdPrefix}-${this.generateIdentifier()}`;
        window.sessionStorage.setItem(UserEventLogsService.tabIdStorageKey, storedTabId);
      }

      this.tabId = storedTabId;

      return this.tabId;
    }

    this.tabId = `${UserEventLogsService.tabIdPrefix}-${this.generateIdentifier()}`;

    return this.tabId;
  }

  private generateSessionId(): string {
    return this.generateIdentifier();
  }

  private generateIdentifier(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private getStoredLastTrackedRouteForCurrentUser(): string | null {
    if (
      !this.currentUserId ||
      typeof window === 'undefined' ||
      !window.sessionStorage
    ) {
      return null;
    }

    const rawValue = window.sessionStorage.getItem(UserEventLogsService.lastTrackedRouteStorageKey);

    if (!rawValue) {
      return null;
    }

    try {
      const parsedValue = JSON.parse(rawValue) as {
        userId?: string;
        routeFull?: string;
      };

      if (
        parsedValue.userId === this.currentUserId &&
        typeof parsedValue.routeFull === 'string' &&
        parsedValue.routeFull.trim()
      ) {
        return parsedValue.routeFull;
      }
    } catch {
      // Ignore malformed storage values.
    }

    return null;
  }

  private setStoredLastTrackedRouteForCurrentUser(routeFull: string): void {
    if (
      !this.currentUserId ||
      typeof window === 'undefined' ||
      !window.sessionStorage
    ) {
      return;
    }

    const payload = JSON.stringify({
      userId: this.currentUserId,
      routeFull
    });

    window.sessionStorage.setItem(UserEventLogsService.lastTrackedRouteStorageKey, payload);
  }

  private getStoredTrackedPageContextForCurrentUser(): ITrackedPageContext | null {
    if (
      !this.currentUserId ||
      typeof window === 'undefined' ||
      !window.sessionStorage
    ) {
      return null;
    }

    const rawValue = window.sessionStorage.getItem(UserEventLogsService.activeTrackedPageStorageKey);

    if (!rawValue) {
      return null;
    }

    try {
      const parsedValue = JSON.parse(rawValue) as {
        userId?: string;
        routeFull?: string;
        routePath?: string;
        startedAtMs?: number;
      };

      if (
        parsedValue.userId !== this.currentUserId ||
        typeof parsedValue.routeFull !== 'string' ||
        !parsedValue.routeFull.trim() ||
        typeof parsedValue.routePath !== 'string' ||
        !parsedValue.routePath.trim() ||
        typeof parsedValue.startedAtMs !== 'number' ||
        Number.isNaN(parsedValue.startedAtMs)
      ) {
        return null;
      }

      return {
        routeFull: parsedValue.routeFull,
        routePath: parsedValue.routePath,
        startedAtMs: parsedValue.startedAtMs
      };
    } catch {
      return null;
    }
  }

  private setStoredTrackedPageContextForCurrentUser(trackedPageContext: ITrackedPageContext): void {
    if (
      !this.currentUserId ||
      typeof window === 'undefined' ||
      !window.sessionStorage
    ) {
      return;
    }

    const payload = JSON.stringify({
      userId: this.currentUserId,
      routeFull: trackedPageContext.routeFull,
      routePath: trackedPageContext.routePath,
      startedAtMs: trackedPageContext.startedAtMs
    });

    window.sessionStorage.setItem(UserEventLogsService.activeTrackedPageStorageKey, payload);
  }

  private clearStoredTrackedPageContext(): void {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }

    window.sessionStorage.removeItem(UserEventLogsService.activeTrackedPageStorageKey);
  }

  private getStoredPendingEventQueue(): IQueuedTrackingEvent[] {
    if (typeof window === 'undefined' || !window.localStorage) {
      return [];
    }

    const rawValue = window.localStorage.getItem(UserEventLogsService.eventQueueStorageKey);

    if (!rawValue) {
      return [];
    }

    try {
      const parsedValue = JSON.parse(rawValue) as Array<Partial<IQueuedTrackingEvent>>;
      if (!Array.isArray(parsedValue)) {
        return [];
      }

      return parsedValue
        .filter(item => {
          return (
            !!item &&
            typeof item.id === 'string' &&
            typeof item.userId === 'string' &&
            !!item.userId.trim() &&
            !!item.payload &&
            typeof item.payload.routeFull === 'string' &&
            typeof item.payload.routePath === 'string'
          );
        })
        .map(item => ({
          id: item.id as string,
          userId: item.userId as string,
          payload: item.payload as ILogRouteNavigationPayload,
          enqueuedAtMs:
            typeof item.enqueuedAtMs === 'number' && !Number.isNaN(item.enqueuedAtMs)
              ? item.enqueuedAtMs
              : Date.now()
        }));
    } catch {
      return [];
    }
  }

  private setStoredPendingEventQueue(queue: IQueuedTrackingEvent[]): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    if (!queue.length) {
      window.localStorage.removeItem(UserEventLogsService.eventQueueStorageKey);

      return;
    }

    window.localStorage.setItem(
      UserEventLogsService.eventQueueStorageKey,
      JSON.stringify(queue)
    );
  }

  private clearStoredLastTrackedRoute(): void {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }

    window.sessionStorage.removeItem(UserEventLogsService.lastTrackedRouteStorageKey);
  }

  private setTrackingResult(result: {
    status: UserEventLogTrackingStatus;
    routeFull: string | null;
    reason?: string;
    error?: any;
  }): IUserEventLogTrackingResult {
    const nextResult: IUserEventLogTrackingResult = {
      status: result.status,
      at: new Date(),
      routeFull: result.routeFull,
      reason: result.reason,
      error: result.error
    };

    this.trackingResultBehavior.next(nextResult);

    return nextResult;
  }
  private getDocsInContext(queryReference: any): Promise<any> {
    return firestoreGetDocs(queryReference);
  }

  private firestoreCollection(path: string): any {
    return firestoreCollection(this.firestore, path);
  }

  private firestoreQuery(reference: any, ...constraints: any[]): any {
    return (firestoreQuery as any)(reference, ...constraints);
  }

  private firestoreWhere(...args: any[]): any {
    return (firestoreWhere as any)(...args);
  }

  private firestoreOrderBy(...args: any[]): any {
    return (firestoreOrderBy as any)(...args);
  }

  private firestoreLimit(...args: any[]): any {
    return (firestoreLimit as any)(...args);
  }

  private firestoreStartAfter(...args: any[]): any {
    return (firestoreStartAfter as any)(...args);
  }

}
